import { useEffect, useRef, useState } from "react";

import { hearthStepCopy, offerByDirection, siteCopy } from "../../content";

import "./hearth.css";

const copy = siteCopy.hearth;

const landingSteps = (["arrive", "make-room", "choose"] as const).map(
  (id) => hearthStepCopy[id],
);

export interface HearthPanelProps {
  onClose?: () => void;
  onHeartToHeart?: () => void;
  embedded?: boolean;
}

export function HearthPanel({ onClose, onHeartToHeart, embedded = false }: HearthPanelProps) {
  const [step, setStep] = useState(0);
  const headingRef = useRef<HTMLHeadingElement>(null);
  const current = landingSteps[step];
  const heartToHeart = offerByDirection["heart-to-heart"];
  const isLast = step === landingSteps.length - 1;
  const PanelTitle = embedded ? "h3" : "h2";
  const MomentTitle = embedded ? "h4" : "h3";

  useEffect(() => {
    headingRef.current?.focus();
  }, [step]);

  if (!current) return null;

  return (
    <section
      className="hearth-panel"
      data-embedded={embedded || undefined}
      aria-labelledby="hearth-title"
    >
      <header className="hearth-panel__header">
        <div>
          <p className="hearth-panel__eyebrow">{copy.eyebrow}</p>
          <PanelTitle className="hearth-panel__title" id="hearth-title">
            {copy.title}
          </PanelTitle>
        </div>
        {onClose && !embedded ? (
          <button className="hearth-panel__quiet-button" type="button" onClick={onClose}>
            Sluiten
          </button>
        ) : null}
      </header>

      <p className="hearth-panel__intro">{copy.intro}</p>

      <div className="hearth-panel__progress">
        <span aria-live="polite">Moment {step + 1} van {landingSteps.length}</span>
        <progress
          aria-label={`Moment ${step + 1} van ${landingSteps.length}`}
          max={landingSteps.length}
          value={step + 1}
        />
      </div>

      <article className="hearth-panel__moment" key={current.id}>
        <span className="hearth-panel__flame" aria-hidden="true">✦</span>
        <div>
          <MomentTitle className="hearth-panel__moment-title" ref={headingRef} tabIndex={-1}>
            {current.title}
          </MomentTitle>
          <p>{current.body}</p>
          <blockquote>{current.prompt}</blockquote>
        </div>
      </article>

      {isLast ? (
        <aside className="hearth-panel__invitation" aria-labelledby="hearth-invitation-title">
          <div>
            <p className="hearth-panel__eyebrow">{copy.invitationEyebrow}</p>
            <MomentTitle id="hearth-invitation-title">{copy.invitationTitle}</MomentTitle>
            <p>{copy.invitationBody}</p>
          </div>
          {onHeartToHeart ? (
            <button className="hearth-panel__offer-link" type="button" onClick={onHeartToHeart}>
              {copy.invitationCtaLabel}
            </button>
          ) : (
            <a
              className="hearth-panel__offer-link"
              href={heartToHeart.href}
              target="_blank"
              rel="noreferrer"
            >
              {heartToHeart.ctaLabel}
              <span aria-hidden="true">↗</span>
            </a>
          )}
        </aside>
      ) : null}

      <div className="hearth-panel__step-actions">
        {step > 0 ? (
          <button
            className="hearth-panel__secondary-button"
            type="button"
            onClick={() => setStep((currentStep) => currentStep - 1)}
          >
            Vorige
          </button>
        ) : <span />}
        {!isLast ? (
          <button
            className="hearth-panel__primary-button"
            type="button"
            onClick={() => setStep((currentStep) => currentStep + 1)}
          >
            Volgende moment
          </button>
        ) : (
          <button
            className="hearth-panel__secondary-button"
            type="button"
            onClick={() => setStep(0)}
          >
            Nog eens landen
          </button>
        )}
      </div>
    </section>
  );
}
